/**
 * PortalCallBuyers — Call tracking buyer management
 *
 * Buyers = call routing partners (Ringba, TrackDrive, Retreaver, Invoca, etc.)
 * Separate from Advertisers (which handle lead/webform submissions).
 */

import React, { useState, useEffect, useCallback } from 'react';
import PortalLayout from '@/components/portal/PortalLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Phone, Plus, Loader2, DollarSign, Pencil, Save, ArrowLeft, Trash2,
  Pause, Play, Clock, Settings,
} from 'lucide-react';
import { supabaseGet, supabasePost, supabasePatch, supabaseDelete } from '@/utils/supabaseRest';

interface CallBuyer {
  id: string;
  name: string;
  company: string | null;
  contact_email: string;
  phone: string | null;
  status: string;
  buyer_type: string;
  call_platform: string | null;
  campaign_url: string | null;
  campaign_id: string | null;
  target_id: string | null;
  ringba_url: string | null;
  retreaver_url: string | null;
  webhook_url: string | null;
  postback_url: string | null;
  display_number: string | null;
  min_call_duration: number;
  call_hours_start: number;
  call_hours_end: number;
  call_hours_timezone: string;
  call_days: string[];
  ivr_enabled: boolean;
  concurrent_cap: number | null;
  caller_id_type: string;
  postback_on: string;
  verticals: string[];
  states: string[];
  fixed_payout: number | null;
  payout_type: string;
  max_daily_volume: number | null;
  current_daily_count: number;
  notes: string | null;
  created_at: string;
}

const PLATFORMS = [
  { value: 'ringba', label: 'Ringba', color: 'text-blue-400' },
  { value: 'trackdrive', label: 'TrackDrive', color: 'text-emerald-400' },
  { value: 'retreaver', label: 'Retreaver', color: 'text-violet-400' },
  { value: 'invoca', label: 'Invoca', color: 'text-amber-400' },
  { value: 'ringpool', label: 'RingPool', color: 'text-pink-400' },
  { value: 'callrail', label: 'CallRail', color: 'text-red-400' },
  { value: 'other', label: 'Other / Direct', color: 'text-slate-400' },
];

const VERTICALS = [
  'auto_insurance', 'home_insurance', 'health_insurance', 'life_insurance',
  'debt_relief', 'debt_consolidation', 'personal_loan', 'mortgage_refi',
  'solar', 'medicare', 'tax_relief', 'home_security',
];

const DAYS = [
  { value: 'mon', label: 'Mon' }, { value: 'tue', label: 'Tue' },
  { value: 'wed', label: 'Wed' }, { value: 'thu', label: 'Thu' },
  { value: 'fri', label: 'Fri' }, { value: 'sat', label: 'Sat' },
  { value: 'sun', label: 'Sun' },
];

interface BuyerDid {
  id: string;
  buyer_id: string;
  did: string;
  description: string | null;
  did_type: string;
  status: string;
  priority: number;
  weight: number;
  concurrent_cap: number | null;
  daily_cap: number | null;
  current_daily_count: number;
  schedule_enabled: boolean;
  schedule_start_hour: number;
  schedule_end_hour: number;
  total_calls: number;
  total_connected: number;
  total_converted: number;
  avg_duration: number;
  last_call_at: string | null;
  is_rtb: boolean;
  rtb_endpoint: string | null;
  rtb_timeout_ms: number;
  rtb_min_bid: number | null;
}

type EditTab = 'general' | 'platform' | 'dids' | 'schedule' | 'targeting' | 'postback';

export default function PortalCallBuyers() {
  const [buyers, setBuyers] = useState<CallBuyer[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<CallBuyer>>({});
  const [editTab, setEditTab] = useState<EditTab>('general');
  const [saving, setSaving] = useState(false);
  const [dids, setDids] = useState<BuyerDid[]>([]);
  const [showAddDid, setShowAddDid] = useState(false);
  const [newDid, setNewDid] = useState('');
  const [newDidType, setNewDidType] = useState('local');
  const [newDidDesc, setNewDidDesc] = useState('');
  const [newIsRtb, setNewIsRtb] = useState(false);
  const [newRtbEndpoint, setNewRtbEndpoint] = useState('');

  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formPlatform, setFormPlatform] = useState('ringba');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setBuyers(((await supabaseGet("buyers?buyer_type=eq.call&order=created_at.desc")) || []) as CallBuyer[]);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!formName || !formEmail) return;
    setCreating(true);
    try {
      await supabasePost('buyers', {
        name: formName, contact_email: formEmail, buyer_type: 'call',
        call_platform: formPlatform, status: 'active', payout_type: 'fixed',
        fixed_payout: 30, max_daily_volume: 50, min_call_duration: 90,
        verticals: ['auto_insurance'],
      });
      setShowCreate(false); setFormName(''); setFormEmail(''); load();
    } catch (err) { console.error(err); }
    finally { setCreating(false); }
  };

  const loadDids = async (buyerId: string) => {
    try {
      const data = await supabaseGet(`buyer_dids?buyer_id=eq.${buyerId}&order=priority.asc`);
      setDids(data || []);
    } catch (err) { console.error(err); }
  };

  const addDid = async (buyerId: string) => {
    if (!newDid) return;
    try {
      await supabasePost('buyer_dids', {
        buyer_id: buyerId, did: newDid, did_type: newDidType,
        description: newDidDesc || null, status: 'active',
        is_rtb: newIsRtb, rtb_endpoint: newIsRtb ? newRtbEndpoint : null,
      });
      setNewDid(''); setNewDidDesc(''); setNewRtbEndpoint(''); setNewIsRtb(false); setShowAddDid(false);
      loadDids(buyerId);
    } catch (err) { console.error(err); }
  };

  const toggleDid = async (did: BuyerDid) => {
    const s = did.status === 'active' ? 'paused' : 'active';
    await supabasePatch(`buyer_dids?id=eq.${did.id}`, { status: s }).catch(() => {});
    setDids(prev => prev.map(d => d.id === did.id ? { ...d, status: s } : d));
  };

  const deleteDid = async (didId: string, buyerId: string) => {
    await supabaseDelete(`buyer_dids?id=eq.${didId}`).catch(() => {});
    setDids(prev => prev.filter(d => d.id !== didId));
  };

  const startEdit = (b: CallBuyer) => { setEditingId(b.id); setEditForm({ ...b }); setEditTab('general'); loadDids(b.id); };

  const saveEdit = async () => {
    if (!editingId) return;
    setSaving(true);
    try {
      const { id, created_at, current_daily_count, ...updates } = editForm as any;
      await supabasePatch(`buyers?id=eq.${editingId}`, updates);
      setBuyers(prev => prev.map(b => b.id === editingId ? { ...b, ...updates } : b));
      setEditingId(null);
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const toggle = async (b: CallBuyer) => {
    const s = b.status === 'active' ? 'paused' : 'active';
    await supabasePatch(`buyers?id=eq.${b.id}`, { status: s }).catch(() => {});
    setBuyers(prev => prev.map(x => x.id === b.id ? { ...x, status: s } : x));
  };

  const u = (k: string, v: any) => setEditForm(f => ({ ...f, [k]: v }));

  // ── Full editor ──
  if (editingId) {
    const plat = PLATFORMS.find(p => p.value === editForm.call_platform);
    return (
      <PortalLayout>
        <div className="max-w-4xl mx-auto space-y-4">
          <div className="flex items-center justify-between">
            <button onClick={() => setEditingId(null)} className="flex items-center gap-1 text-sm text-slate-400 hover:text-white"><ArrowLeft className="h-4 w-4" /> Back</button>
            <Button onClick={saveEdit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Save</Button>
          </div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold text-white">{editForm.name}</h1>
            {plat && <span className={`px-2 py-0.5 rounded text-xs font-medium bg-slate-700 ${plat.color}`}>{plat.label}</span>}
          </div>

          <div className="flex gap-1 flex-wrap border-b border-slate-700 pb-2">
            {([['general', 'General', Phone], ['platform', 'Platform Config', Settings], ['dids', `DIDs (${dids.length})`, Phone], ['schedule', 'Call Hours', Clock], ['targeting', 'Targeting', Phone], ['postback', 'Postback', Phone]] as [EditTab, string, any][]).map(([k, l, I]) => (
              <button key={k} onClick={() => setEditTab(k)} className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium ${editTab === k ? 'bg-emerald-500/20 text-emerald-400' : 'text-slate-400 hover:text-white'}`}>
                <I className="h-3.5 w-3.5" /> {l}
              </button>
            ))}
          </div>

          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-5 space-y-4">
              {editTab === 'general' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <F label="Buyer Name" value={editForm.name} onChange={v => u('name', v)} />
                    <F label="Company" value={editForm.company} onChange={v => u('company', v)} />
                    <F label="Email" value={editForm.contact_email} onChange={v => u('contact_email', v)} />
                    <F label="Phone" value={editForm.phone} onChange={v => u('phone', v)} />
                    <F label="Payout per Call ($)" value={editForm.fixed_payout} onChange={v => u('fixed_payout', parseFloat(v) || 0)} />
                    <F label="Daily Cap" value={editForm.max_daily_volume} onChange={v => u('max_daily_volume', parseInt(v) || null)} placeholder="Unlimited" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">Verticals</label>
                    <div className="flex flex-wrap gap-1.5">
                      {VERTICALS.map(v => (
                        <button key={v} onClick={() => {
                          const cur = editForm.verticals || [];
                          u('verticals', cur.includes(v) ? cur.filter((x: string) => x !== v) : [...cur, v]);
                        }} className={`px-2 py-1 rounded text-xs ${(editForm.verticals || []).includes(v) ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40' : 'bg-slate-800 text-slate-400 border border-slate-700'}`}>
                          {v.replace(/_/g, ' ')}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">Notes</label>
                    <textarea value={editForm.notes || ''} onChange={e => u('notes', e.target.value)} rows={3} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" />
                  </div>
                </div>
              )}

              {editTab === 'platform' && (
                <div className="space-y-4">
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">Call Platform</label>
                    <select value={editForm.call_platform || 'ringba'} onChange={e => u('call_platform', e.target.value)} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm">
                      {PLATFORMS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                  </div>

                  {(editForm.call_platform === 'ringba' || !editForm.call_platform) && (
                    <>
                      <F label="Ringba Campaign URL" value={editForm.ringba_url || editForm.campaign_url} onChange={v => { u('ringba_url', v); u('campaign_url', v); }} placeholder="https://display.ringba.com/enrich/v1/pub/XXXXXX" />
                      <F label="Campaign ID" value={editForm.campaign_id} onChange={v => u('campaign_id', v)} placeholder="CAMP-XXXXXX" />
                      <F label="Target ID" value={editForm.target_id} onChange={v => u('target_id', v)} placeholder="TGT-XXXXXX" />
                      <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-xs text-blue-400">
                        <strong>Ringba Setup:</strong> Get your Campaign URL from Ringba → Campaigns → select campaign → Publisher Numbers. The URL should start with <code>https://display.ringba.com</code>
                      </div>
                    </>
                  )}

                  {editForm.call_platform === 'trackdrive' && (
                    <>
                      <F label="TrackDrive API URL" value={editForm.campaign_url} onChange={v => u('campaign_url', v)} placeholder="https://api.trackdrive.com/api/v1/..." />
                      <F label="Campaign ID" value={editForm.campaign_id} onChange={v => u('campaign_id', v)} />
                      <F label="Webhook URL (for call events)" value={editForm.webhook_url} onChange={v => u('webhook_url', v)} placeholder="https://api.trackdrive.com/..." />
                      <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-xs text-emerald-400">
                        <strong>TrackDrive Setup:</strong> Get your API key from TrackDrive → Settings → API. Create a campaign and note the Campaign ID.
                      </div>
                    </>
                  )}

                  {editForm.call_platform === 'retreaver' && (
                    <>
                      <F label="Retreaver Campaign URL" value={editForm.retreaver_url || editForm.campaign_url} onChange={v => { u('retreaver_url', v); u('campaign_url', v); }} placeholder="https://api.retreaver.com/..." />
                      <F label="Campaign ID" value={editForm.campaign_id} onChange={v => u('campaign_id', v)} />
                      <div className="p-3 bg-violet-500/10 border border-violet-500/30 rounded-lg text-xs text-violet-400">
                        <strong>Retreaver Setup:</strong> Get your campaign URL from Retreaver → Campaigns. Tags use bracket syntax: <code>tags[key]=value</code>
                      </div>
                    </>
                  )}

                  {(editForm.call_platform === 'invoca' || editForm.call_platform === 'callrail' || editForm.call_platform === 'other') && (
                    <>
                      <F label="Campaign/Tracking URL" value={editForm.campaign_url} onChange={v => u('campaign_url', v)} placeholder="https://..." />
                      <F label="Campaign ID" value={editForm.campaign_id} onChange={v => u('campaign_id', v)} />
                      <F label="Webhook URL" value={editForm.webhook_url} onChange={v => u('webhook_url', v)} placeholder="https://..." />
                    </>
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-slate-400 block mb-1">Caller ID Type</label>
                      <select value={editForm.caller_id_type || 'dynamic'} onChange={e => u('caller_id_type', e.target.value)} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm">
                        <option value="dynamic">Dynamic (rotated)</option>
                        <option value="fixed">Fixed Number</option>
                        <option value="toll_free">Toll-Free</option>
                      </select>
                    </div>
                    <F label="Display Number" value={editForm.display_number} onChange={v => u('display_number', v)} placeholder="+18005551234" />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <F label="Min Call Duration (seconds)" value={editForm.min_call_duration} onChange={v => u('min_call_duration', parseInt(v) || 90)} />
                    <F label="Concurrent Call Cap" value={editForm.concurrent_cap} onChange={v => u('concurrent_cap', parseInt(v) || null)} placeholder="Unlimited" />
                  </div>

                  <label className="flex items-center gap-2 text-sm text-slate-300">
                    <input type="checkbox" checked={editForm.ivr_enabled || false} onChange={e => u('ivr_enabled', e.target.checked)} className="rounded border-slate-600" />
                    IVR enabled (caller hears menu before connecting)
                  </label>
                </div>
              )}

              {/* ── DIDs Tab ── */}
              {editTab === 'dids' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-slate-400">Phone numbers (DIDs) or RTB endpoints that calls route to for this buyer.</p>
                    </div>
                    <button onClick={() => setShowAddDid(!showAddDid)} className="flex items-center gap-1 px-3 py-1.5 rounded bg-emerald-600 text-xs text-white hover:bg-emerald-700">
                      <Plus className="h-3 w-3" /> Add DID
                    </button>
                  </div>

                  {showAddDid && (
                    <Card className="bg-slate-900/50 border-emerald-500/30">
                      <CardContent className="p-4 space-y-3">
                        <div className="grid grid-cols-3 gap-3">
                          <F label="Phone Number / DID *" value={newDid} onChange={setNewDid} placeholder="+18005551234" />
                          <div>
                            <label className="text-xs text-slate-400 block mb-1">Type</label>
                            <select value={newDidType} onChange={e => setNewDidType(e.target.value)} className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm">
                              <option value="local">Local</option>
                              <option value="toll_free">Toll-Free</option>
                              <option value="international">International</option>
                            </select>
                          </div>
                          <F label="Description" value={newDidDesc} onChange={setNewDidDesc} placeholder="Main line" />
                        </div>
                        <label className="flex items-center gap-2 text-sm text-slate-300">
                          <input type="checkbox" checked={newIsRtb} onChange={e => setNewIsRtb(e.target.checked)} className="rounded border-slate-600" />
                          This is an RTB (Real-Time Bidding) endpoint
                        </label>
                        {newIsRtb && (
                          <F label="RTB Endpoint URL" value={newRtbEndpoint} onChange={setNewRtbEndpoint} placeholder="https://rtb.buyer.com/bid?did={did}" />
                        )}
                        <F label="Webhook URL (optional — fires on call connect)" value="" onChange={() => {}} placeholder="https://buyer.com/webhook?call_id={call_id}" />
                        <div className="flex gap-2">
                          <button onClick={() => editingId && addDid(editingId)} className="px-3 py-1.5 rounded bg-emerald-600 text-xs text-white hover:bg-emerald-700">Add</button>
                          <button onClick={() => setShowAddDid(false)} className="px-3 py-1.5 rounded bg-slate-700 text-xs text-slate-300">Cancel</button>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {dids.length === 0 ? (
                    <div className="border-2 border-dashed border-slate-700 rounded-lg p-8 text-center text-sm text-slate-500">
                      No DIDs configured. Click "Add DID" to add phone numbers or RTB endpoints.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {dids.map(d => (
                        <Card key={d.id} className={`border ${d.status === 'active' ? 'bg-slate-900/50 border-slate-700' : 'bg-slate-900/50 border-slate-800 opacity-50'}`}>
                          <CardContent className="p-3">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0">
                                <Phone className="h-4 w-4 text-emerald-400" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium text-white font-mono">{d.did}</span>
                                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${d.did_type === 'toll_free' ? 'bg-blue-500/20 text-blue-400' : 'bg-slate-600/20 text-slate-400'}`}>{d.did_type}</span>
                                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${d.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>{d.status}</span>
                                  {d.is_rtb && <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-500/20 text-amber-400">RTB</span>}
                                </div>
                                <div className="text-[10px] text-slate-500 mt-0.5 flex gap-3">
                                  {d.description && <span>{d.description}</span>}
                                  <span>{d.total_calls} calls</span>
                                  <span>{d.total_connected} connected</span>
                                  <span>{d.total_converted} converted</span>
                                  {d.daily_cap && <span>{d.current_daily_count}/{d.daily_cap}/day</span>}
                                  {d.is_rtb && d.rtb_endpoint && <span className="text-amber-400 truncate max-w-[200px]">{d.rtb_endpoint}</span>}
                                </div>
                              </div>
                              <div className="flex items-center gap-1.5 shrink-0">
                                <button onClick={() => toggleDid(d)} className="px-2 py-1 rounded bg-slate-700 text-xs text-slate-300 hover:bg-slate-600">
                                  {d.status === 'active' ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
                                </button>
                                <button onClick={() => editingId && deleteDid(d.id, editingId)} className="text-slate-600 hover:text-red-400"><Trash2 className="h-3 w-3" /></button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {editTab === 'schedule' && (
                <div className="space-y-4">
                  <p className="text-xs text-slate-400">Set the hours when calls can be routed to this buyer.</p>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs text-slate-400 block mb-1">Start Hour</label>
                      <select value={editForm.call_hours_start || 8} onChange={e => u('call_hours_start', parseInt(e.target.value))} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm">
                        {Array.from({ length: 24 }, (_, i) => <option key={i} value={i}>{i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-slate-400 block mb-1">End Hour</label>
                      <select value={editForm.call_hours_end || 20} onChange={e => u('call_hours_end', parseInt(e.target.value))} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm">
                        {Array.from({ length: 24 }, (_, i) => <option key={i} value={i}>{i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-slate-400 block mb-1">Timezone</label>
                      <select value={editForm.call_hours_timezone || 'America/New_York'} onChange={e => u('call_hours_timezone', e.target.value)} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm">
                        <option value="America/New_York">Eastern</option>
                        <option value="America/Chicago">Central</option>
                        <option value="America/Denver">Mountain</option>
                        <option value="America/Los_Angeles">Pacific</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 block mb-2">Active Days</label>
                    <div className="flex gap-2">
                      {DAYS.map(d => (
                        <button key={d.value} onClick={() => {
                          const days = editForm.call_days || ['mon','tue','wed','thu','fri'];
                          u('call_days', days.includes(d.value) ? days.filter((x: string) => x !== d.value) : [...days, d.value]);
                        }} className={`px-3 py-2 rounded-lg text-xs font-medium ${(editForm.call_days || ['mon','tue','wed','thu','fri']).includes(d.value) ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40' : 'bg-slate-800 text-slate-500 border border-slate-700'}`}>
                          {d.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {editTab === 'targeting' && (
                <div className="space-y-4">
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">Accepted States (empty = all)</label>
                    <input value={(editForm.states || []).join(', ')} onChange={e => u('states', e.target.value.split(',').map((s: string) => s.trim().toUpperCase()).filter(Boolean))} placeholder="CA, TX, FL, NY" className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" />
                    <div className="text-[10px] text-slate-500 mt-1">Comma-separated state codes. Leave empty to accept all states.</div>
                  </div>
                </div>
              )}

              {editTab === 'postback' && (
                <div className="space-y-4">
                  <p className="text-xs text-slate-400">Configure when and where call conversion data is sent back.</p>
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">Fire Postback On</label>
                    <select value={editForm.postback_on || 'connected'} onChange={e => u('postback_on', e.target.value)} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm">
                      <option value="connected">Call Connected</option>
                      <option value="duration">Min Duration Met ({editForm.min_call_duration || 90}s)</option>
                      <option value="converted">Sale Confirmed</option>
                      <option value="all">All Events</option>
                    </select>
                  </div>
                  <F label="Postback URL" value={editForm.postback_url} onChange={v => u('postback_url', v)} placeholder="https://tracking.com/postback?call_id={call_id}&duration={duration}&payout={payout}" />
                  <div className="text-[10px] text-slate-500">
                    Macros: {'{call_id}'}, {'{session_id}'}, {'{duration}'}, {'{payout}'}, {'{caller_number}'}, {'{disposition}'}, {'{buyer_name}'}, {'{vertical}'}, {'{affiliate_id}'}
                  </div>
                  <div className="p-3 bg-slate-900 border border-slate-700 rounded-lg">
                    <div className="text-[10px] text-slate-500 mb-1">Your call postback URL (give to the platform):</div>
                    <div className="text-xs text-emerald-400 font-mono break-all">
                      https://intentdrive.ai/api/postback?session_id={'{session_id}'}&payout={'{payout}'}&status=converted&txn_id={'{call_id}'}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </PortalLayout>
    );
  }

  // ── List view ──
  return (
    <PortalLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Phone className="h-6 w-6 text-emerald-400" /> Buyers (Call)
            </h1>
            <p className="text-sm text-slate-400 mt-1">Manage call routing buyers — Ringba, TrackDrive, Retreaver, and other call tracking platforms.</p>
          </div>
          <Button onClick={() => setShowCreate(!showCreate)} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="h-4 w-4 mr-1" /> Add Buyer
          </Button>
        </div>

        {showCreate && (
          <Card className="bg-slate-800/50 border-emerald-500/30">
            <CardContent className="p-5 space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <F label="Buyer Name *" value={formName} onChange={setFormName} placeholder="National Auto Insurance" />
                <F label="Email *" value={formEmail} onChange={setFormEmail} placeholder="buyer@company.com" />
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Platform</label>
                  <select value={formPlatform} onChange={e => setFormPlatform(e.target.value)} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm">
                    {PLATFORMS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
              </div>
              <Button onClick={create} disabled={creating || !formName || !formEmail} className="bg-emerald-600 hover:bg-emerald-700">{creating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Create</Button>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 text-emerald-400 animate-spin" /></div>
        ) : buyers.length === 0 ? (
          <Card className="bg-slate-800/50 border-slate-700"><CardContent className="p-8 text-center"><Phone className="h-10 w-10 text-slate-600 mx-auto mb-3" /><p className="text-sm text-slate-400">No call buyers yet. Add your first Ringba, TrackDrive, or Retreaver buyer.</p></CardContent></Card>
        ) : (
          <div className="space-y-2">
            {buyers.map(b => {
              const plat = PLATFORMS.find(p => p.value === b.call_platform);
              return (
                <Card key={b.id} className={`border ${b.status === 'active' ? 'bg-slate-800/50 border-slate-700' : 'bg-slate-900/50 border-slate-800 opacity-60'}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0">
                        <Phone className="h-5 w-5 text-emerald-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-white">{b.name}</span>
                          {plat && <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-700 ${plat.color}`}>{plat.label}</span>}
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${b.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-600/20 text-slate-400'}`}>{b.status}</span>
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-500/20 text-amber-400">${b.fixed_payout || 0}/call</span>
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-3">
                          <span>{b.contact_email}</span>
                          <span>{b.min_call_duration}s min</span>
                          {b.max_daily_volume && <span>{b.current_daily_count}/{b.max_daily_volume}/day</span>}
                          <span>{b.call_hours_start}:00-{b.call_hours_end}:00 {b.call_hours_timezone?.split('/')[1]?.replace('_',' ') || ''}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button onClick={() => startEdit(b)} className="flex items-center gap-1 px-2 py-1 rounded bg-emerald-600/20 text-xs text-emerald-400 hover:bg-emerald-600/30"><Pencil className="h-3 w-3" /> Edit</button>
                        <button onClick={() => toggle(b)} className="px-2 py-1 rounded bg-slate-700 text-xs text-slate-300 hover:bg-slate-600">{b.status === 'active' ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}</button>
                        <button onClick={async () => { await supabaseDelete(`buyers?id=eq.${b.id}`).catch(() => {}); setBuyers(prev => prev.filter(x => x.id !== b.id)); }} className="text-slate-600 hover:text-red-400"><Trash2 className="h-3 w-3" /></button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </PortalLayout>
  );
}

function F({ label, value, onChange, placeholder }: { label: string; value: any; onChange: (v: string) => void; placeholder?: string }) {
  return <div><label className="text-xs text-slate-400 block mb-1">{label}</label><input value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" /></div>;
}
